import type { Dispatch, ReactNode, SetStateAction } from "react";
import type React from "react";

import { useEffect, useLayoutEffect, useMemo } from "react";
import { createContext, useRef, useState } from "react";

import type { Feedback } from "@/features/request/feedback-tool/feedbackTool/types";

import { useWindowSize } from "@/features/request/feedback-tool/feedbackTool/hooks/useWindowSize.tsx";
import { CONTROLS_DEFAULT } from "@/features/request/feedback-tool/feedbackTool/utils";

interface FeedbackToolContextType {
  animationZoom: string;
  applyZoom: (newZoom: number, newControls: number) => void;
  audioRef: React.MutableRefObject<HTMLAudioElement | null>;
  controls: number;
  deleteFeedBack: boolean;
  divImgRef: React.RefObject<HTMLDivElement>;
  feedback: Array<Feedback> | null;
  formFeedBack:
    | ({ type: "ADD" | "EDIT" | "VIEW" | undefined } & Feedback)
    | null;
  imageOnly: boolean;
  img: { asset: string; id: number; url: string };
  imgRef: React.MutableRefObject<HTMLImageElement | null>;
  isTablet: boolean;
  openModal: boolean;
  pdfContainerRef: React.RefObject<HTMLDivElement>;
  pdfScrollerRef: React.RefObject<HTMLDivElement>;
  resetZoomAndControls: () => void;
  selected: Feedback | null;
  setAnimationZoom: Dispatch<SetStateAction<string>>;
  setDeleteFeedBack: Dispatch<SetStateAction<boolean>>;
  setFeedBack: Dispatch<SetStateAction<Array<Feedback> | null>>;
  setFormFeedBack: Dispatch<
    SetStateAction<
      ({ type: "ADD" | "EDIT" | "VIEW" | undefined } & Feedback) | null
    >
  >;
  setImageOnly: Dispatch<SetStateAction<boolean>>;
  setImg: Dispatch<SetStateAction<{ asset: string; id: number; url: string }>>;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setSelected: Dispatch<SetStateAction<Feedback | null>>;
  setSize: Dispatch<SetStateAction<"FULL" | "MODAL">>;
  setZoom: Dispatch<SetStateAction<number>>;
  size: "FULL" | "MODAL";
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  zoom: number;
}

export const FeedbackToolContext = createContext<
  FeedbackToolContextType | undefined
>(undefined);

export function FeedbackToolProvider({ children }: { children: ReactNode }) {
  const divImgRef = useRef<HTMLDivElement>(null);
  const screenSize = useWindowSize();
  const [openModal, setOpen] = useState(false);
  const [img, setImg] = useState({
    asset: "",
    id: 0,
    url: "",
  });
  const [feedback, setFeedBack] = useState<Array<Feedback> | null>(null);
  const [formFeedBack, setFormFeedBack] = useState<
    ({ type: "ADD" | "EDIT" | "VIEW" | undefined } & Feedback) | null
  >(null);
  const [selected, setSelected] = useState<Feedback | null>(null);
  const [deleteFeedBack, setDeleteFeedBack] = useState<boolean>(false);
  const [size, setSize] = useState<"FULL" | "MODAL">("MODAL");
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [controls, setControls] = useState<number>(CONTROLS_DEFAULT);
  const [animationZoom, setAnimationZoom] = useState("");
  // Floor keeps the base zoom positive on phone-sized viewports.
  const baseZoom = Math.max(screenSize.width - 400, 320);
  const [zoom, setZoom] = useState(baseZoom);
  const [imageOnly, setImageOnly] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const pdfContainerRef = useRef<HTMLDivElement | null>(null);
  const pdfScrollerRef = useRef<HTMLDivElement | null>(null);
  const { width } = useWindowSize();
  const isTablet = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(pointer: coarse)").matches,
    [],
  );

  useEffect(() => {
    if (width < 1080 || isTablet) {
      setSize("FULL");
    }
  }, [width, isTablet]);

  useEffect(() => {
    if (size === "MODAL") {
      resetZoomAndControls();
    }
  }, [size]);

  useLayoutEffect(() => {
    setFeedBack(null);
    setSelected(null);
    setFormFeedBack(null);
  }, [img.id, openModal]);

  const applyZoom = (newZoom: number, newControls: number) => {
    setZoom(newZoom);
    setControls(newControls);
  };

  const resetZoomAndControls = () => {
    setZoom(baseZoom);
    setControls(CONTROLS_DEFAULT);
    setImageOnly(false);
  };

  return (
    <FeedbackToolContext.Provider
      value={{
        animationZoom,
        applyZoom,
        audioRef,
        controls,
        deleteFeedBack,
        divImgRef,
        feedback,
        formFeedBack,
        imageOnly,
        img,
        imgRef,
        isTablet,
        openModal,
        pdfContainerRef,
        pdfScrollerRef,
        resetZoomAndControls,
        selected,
        setAnimationZoom,
        setDeleteFeedBack,
        setFeedBack,
        setFormFeedBack,
        setImageOnly,
        setImg,
        setOpen,
        setSelected,
        setSize,
        setZoom,
        size,
        videoRef,
        zoom,
      }}
    >
      {children}
    </FeedbackToolContext.Provider>
  );
}
